import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isEmail, ReactMultiEmail } from "react-multi-email";
import "react-multi-email/style.css";
import { shallowEqual, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { RootState } from "../../../../../redux/rootReducer";
import TeamsService from "../../services/teamsService";
import { handleError } from "../../../../../utils/helpers/helpers";

const initialValues = {
  emails: [],
};

interface AddToTeamProps {
  intl?: any;
  teamId: number;
}

const AddToTeam = ({ intl, teamId }: AddToTeamProps) => {
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [checkIfIsEmail, setCheckIfIsEmail] = useState(false);
  const [validateEmail, setValidateEmail] = useState(true);

  useEffect(() => {
    // added onkeyup event listener as the library does not support onkeyup event
    let el = document.getElementsByClassName("emails-wrapper");
    let inputElement = el[0].firstChild.firstChild.nextSibling;
    inputElement.addEventListener("keyup", (e) => {
      getInputOnKeyUp(e);
    });
  }, []);

  function getInputOnKeyUp(e) {
    if (e.target.value.length > 0) {
      setValidateEmail(false);
    } else {
      setValidateEmail(true);
    }
  }
  const Schema = Yup.object().shape({
    emails: Yup.array(),
  });

  const { user } = useSelector(
    ({ auth }: RootState) => ({
      user: auth.user,
    }),
    shallowEqual
  );

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Schema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      if (checkIfIsEmail) {
        setSubmitting(true);
        setStatus();
        enableLoading();
        TeamsService.addTeamMembers(teamId, {
          emails: emails,
        })
          .then(({ data }) => {
            document.getElementById("modalDismissId").click();
          })
          .catch((err) => {
            setStatus(handleError(err));
          })
          .finally(() => {
            disableLoading();
            setSubmitting(false);
          });
      } else {
        setStatus(<FormattedMessage id="TEAM.MEMBERS.VALIDATION.EMAIL" />);
        setSubmitting(false);
        disableLoading();
      }
    },
  });

  return (
    <form
      id="kt_addToTeam_form"
      className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp w-75"
      onSubmit={formik.handleSubmit}
    >
      {formik.status ? (
        <div className="mt-8 mb-10 alert alert-custom alert-light-danger alert-dismissible">
          <div className="alert-text font-weight-bold">
            {intl.formatMessage({
              id: formik.status,
              defaultMessage: intl.formatMessage({ id: "GENERIC_ERROR" }),
            })}
          </div>
        </div>
      ) : (
        <div></div>
      )}

      <div className="form-group fv-plugins-icon-container">
        <label className="font-weight-bold mt-8 h5">
          <FormattedMessage id="AUTH.ONBOARDING.ADD_TEAM.INVITE_TITLE" />
        </label>
        <div className="emails-wrapper">
          <ReactMultiEmail
            className={`form-control h-auto py-3 px-6 ${getInputClasses(
              "emails"
            )}`}
            placeholder={
              <FormattedMessage id="AUTH.ONBOARDING.ADD_TEAM.EMAIL_PLACEHOLDER" />
            }
            emails={emails}
            onChange={(_emails: string[]) => {
              setEmails([..._emails]);
            }}
            validateEmail={(email) => {
              let checkMail = isEmail(email); // return boolean
              if (checkMail) {
                setCheckIfIsEmail(true);
              } else {
                setCheckIfIsEmail(false);
              }
              return checkMail;
            }}
            getLabel={(
              email: string,
              index: number,
              removeEmail: (index: number) => void
            ) => {
              return (
                <div data-tag key={index}>
                  {email}
                  <span data-tag-handle onClick={() => removeEmail(index)}>
                    ×
                  </span>
                </div>
              );
            }}
          />
          {formik.touched.emails && formik.errors.emails ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.emails}</div>
            </div>
          ) : null}
        </div>
        <button
          disabled={validateEmail}
          className="btn btn-primary btn-block mt-6"
        >
          <FormattedMessage id="AUTH.GENERAL.SUBMIT_BUTTON" />
        </button>
      </div>
    </form>
  );
};

export default AddToTeam;
